import { useEffect, useRef, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InteractionStatus } from "@azure/msal-browser";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth/useAuth";
import { createApiClient } from "@/lib/api/chatApi";
import { computeContextKey, contextoEnlaceValido, getStoredThread, type LinkContext } from "@/lib/context";

export default function LinkPage() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user, getToken, logout, inProgress } = useAuth();
  const api = useMemo(() => createApiClient(getToken, logout), [getToken, logout]);
  const processedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!widgetId || !isAuthenticated || processedRef.current) return;
    if (inProgress !== InteractionStatus.None) return;
    processedRef.current = true;

    (async () => {
      try {
        const config = await api.getWidgetConfig(widgetId);
        // Solo los parametros que declara el widget, saneados: lo demas (utm_*,
        // un valor con markdown o con la marca del contexto) ni se pinta ni llega
        // al agente, y tampoco cambia la clave del hilo.
        const context = contextoEnlaceValido(searchParams, config.context_schema);
        const email = user?.email || "";
        const ctxKey = await computeContextKey(email, config.agent_id, context);

        // If thread already exists and not expired, reuse it
        const existing = getStoredThread(ctxKey, config.thread_ttl_hours);
        if (existing) {
          navigate(`/?thread=${existing.threadId}&agent=${config.agent_id}`, { replace: true });
          return;
        }

        // Navigate to ChatPage with context in router state
        const linkContext: LinkContext | undefined = (config.context_prompt_template && Object.keys(context).length > 0)
          ? {
              contextPromptTemplate: config.context_prompt_template,
              context,
              ctxKey,
              ttlHours: config.thread_ttl_hours,
              // Para enseñar el contexto con nombre ("ID del caso WISE"), no con la clave
              labels: Object.fromEntries((config.context_schema ?? []).map((f) => [f.key, f.label])),
              autoStart: config.auto_start === true,
            }
          : undefined;

        navigate(`/?agent=${config.agent_id}`, { replace: true, state: { linkContext } });
      } catch (err) {
        console.error("Link init error:", err);
        setError(t("link.error"));
      }
    })();
  }, [widgetId, isAuthenticated, inProgress, api, user, searchParams, navigate, t]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center gap-2">
      <Loader2 className="h-5 w-5 animate-spin text-[var(--muted-foreground)]" />
      <span className="text-sm text-[var(--muted-foreground)]">{t("link.loading")}</span>
    </div>
  );
}
